import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtMoney, fmtPct, fmtNum, monthLabel } from "@/lib/format";
import { PageHeader } from "@/components/common/Page";
import { DataTable } from "@/components/common/DataTable";
import { CalculationDrawer } from "@/components/common/CalculationDrawer";
import { Button } from "@/components/ui/button";
import { Loader2, Eye } from "lucide-react";

export default function CustomerProfitability() {
  const navigate = useNavigate();
  const [drawer, setDrawer] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["cust-profit"], queryFn: async () => (await api.get("/profitability/customers")).data });
  if (isLoading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-cyan-400" /></div>;

  const columns = [
    { key: "customer_name", header: "Customer", render: (r) => <span className="font-semibold text-slate-100">{r.customer_name}</span> },
    { key: "vessels", header: "Vessels", align: "right", mono: true, render: (r) => fmtNum(r.vessels) },
    { key: "users", header: "Users", align: "right", mono: true, render: (r) => fmtNum(r.users) },
    { key: "revenue", header: "Revenue", align: "right", mono: true, render: (r) => fmtMoney(r.revenue) },
    { key: "cost_to_serve", header: "Cost to Serve", align: "right", mono: true, render: (r) => fmtMoney(r.cost_to_serve) },
    { key: "contribution", header: "Contribution", align: "right", mono: true, render: (r) => <span className={r.contribution >= 0 ? "text-emerald-400" : "text-rose-400"}>{fmtMoney(r.contribution)}</span> },
    { key: "margin_pct", header: "Margin", align: "right", mono: true, render: (r) => <span className={r.margin_pct >= 0 ? "text-emerald-400" : "text-rose-400"}>{fmtPct(r.margin_pct)}</span> },
    { key: "calc", header: "", align: "right", render: (r) => (
      <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs text-cyan-400 hover:text-cyan-300" onClick={(e) => { e.stopPropagation(); setDrawer(r); }} data-testid={`view-calc-${r.customer_id}`}>
        <Eye className="h-3.5 w-3.5" /> View calculation
      </Button>
    ) },
  ];

  return (
    <div className="ns-fade">
      <PageHeader title="Customer Profitability" subtitle={`Cost-to-serve & contribution · ${monthLabel(data.month)}`} testId="customer-profitability-header" />
      <DataTable columns={columns} rows={data.customers} testId="customer-profitability-table"
        onRowClick={(r) => navigate(`/customers/${r.customer_id}`)}
        empty={{ title: "No customers yet", message: "Add customers to see profitability and cost-to-serve." }} />
      <CalculationDrawer
        open={!!drawer}
        onOpenChange={(o) => !o && setDrawer(null)}
        title={drawer ? `${drawer.customer_name} — Cost to Serve` : ""}
        components={drawer?.cost_components || []}
        total={drawer?.cost_to_serve}
        resultLabel="Total Cost to Serve"
        extra={drawer && (
          <div className="mt-4 space-y-2 rounded-lg border border-[#1E2D4A] bg-[#17233B]/60 p-3 text-sm">
            <div className="flex justify-between"><span className="text-slate-400">Monthly Revenue</span><span className="font-mono-fin text-slate-100">{fmtMoney(drawer.revenue)}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Contribution</span><span className={`font-mono-fin ${drawer.contribution >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{fmtMoney(drawer.contribution)}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Margin</span><span className="font-mono-fin text-slate-100">{fmtPct(drawer.margin_pct)}</span></div>
          </div>
        )}
      />
    </div>
  );
}
